import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';
import { cn } from '@utils/cn';
import { useCampaignStore } from '@stores/campaign.store';
import { LanguageSwitcher } from '@components/ui/LanguageSwitcher';

export function Navbar() {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const { isMobileMenuOpen, setMobileMenuOpen } = useCampaignStore();
  const location = useLocation();

  // Check if we're on the home page (hero section)
  const isHomePage = location.pathname === '/';

  const navItems = [
    { label: t('nav.about'), href: '/about', isButton: false },
    { label: t('nav.vision'), href: '/vision', isButton: false },
    { label: t('nav.getInvolved'), href: '/volunteer', isButton: false },
    // { label: t('nav.testimonials'), href: '/testimonials', isButton: false },
    { label: t('nav.news'), href: '/news', isButton: false },
    //{ label: t('nav.donate'), href: '/donate', isButton: true },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 50;
      setIsScrolled(scrolled);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    // Reset scroll detection when route changes
    setIsScrolled(window.scrollY > 50);
  }, [location, setMobileMenuOpen]);

  // Determine if navbar should be transparent
  // Only transparent on home page AND when not scrolled
  const isTransparent = isHomePage && !isScrolled;

  return (
    <>
      <nav
        className={cn(
          'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
          isTransparent
            ? 'bg-transparent py-4' 
            : 'bg-white shadow-md py-3'
        )}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group">
              {/* <svg
                width="40"
                height="40"
                viewBox="0 0 100 100"
                className="transition-transform group-hover:scale-110"
              >
                <polygon points="0,0 100,50 0,100" fill="#0284c7" />
                <polygon points="0,20 60,50 0,80" fill="#000" />
                <circle cx="25" cy="50" r="10" fill="#fff" />
              </svg> */}
              <span
                className="font-heading font-black text-2xl transition-colors"
                style={{ color: isTransparent ? '#0284c7' : '#0284c7' }}
              >
                RUI <span style={{ color: isTransparent ? '#1e293b' : '#1a1a1a' }}>2027</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              {navItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  style={{
                    color: item.isButton ? '#ffffff' : (isTransparent ? '#1e293b' : '#1a1a1a'),
                    backgroundColor: item.isButton ? '#0284c7' : 'transparent',
                  }}
                  className={cn(
                    'font-heading font-semibold text-sm uppercase tracking-wider transition-all relative px-4 py-2 rounded-full',
                    !item.isButton && (isTransparent ? 'hover:text-sky-600' : 'hover:text-sky-600'),
                    !item.isButton && !isTransparent && 'hover:bg-gray-100'
                  )}
                >
                  {item.label}
                </Link>
              ))}

              {/* Language Switcher */}
              <div className="pl-4 border-l border-gray-200">
                <LanguageSwitcher isScrolled={!isTransparent} />
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!isMobileMenuOpen)}
              style={{ color: isTransparent ? '#1e293b' : '#1a1a1a' }}
            >
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-white pt-20">
          <div className="px-4 py-4 space-y-4">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setMobileMenuOpen(false)}
                className={cn(
                  'block font-heading font-semibold text-lg uppercase tracking-wider px-4 py-3 rounded-lg',
                  item.isButton
                    ? 'bg-sky-600 text-white text-center'
                    : 'text-campaign-black hover:bg-gray-100'
                )}
              >
                {item.label}
              </Link>
            ))}

            {/* Mobile Language Switcher */}
            <div className="pt-4 border-t border-gray-100 px-4">
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                {t('language.title')}
              </p>
              <LanguageSwitcher variant="buttons" isScrolled={true} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}